/* **The Talia card** — `docs/design/karty-zadan-2026-09-24/canvas.css` `.cv-wide`, with the owner's
 * 24 September 2026 sizes from `zk.css`: the Przegląd's card one height for every kind (`.zp-card
 * .cv-wide { min-height: 286px }`), the Pulpit's shorter card on the bento row's lighter surface with
 * no frame (`.zh-short .cv-wide`).
 *
 * Text that must be read is `--text-2` where the canon writes `--text-3`: `--text-3` measures 3.0:1
 * (Flame) and 4.0:1 (Flame Dark) on these surfaces — the standing deviation `DetailTab.module.css`
 * records, kept by the 31.08 decision on a measured contrast number. */

.card {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: var(--space-14px) var(--space-30px);
  min-width: 0;
  padding: var(--space-4) var(--space-20px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-20px);
}

/* One height for every kind: switching tabs must not move the session table under the card. Every
 * block's `quick` density fits it (owner, 25 September 2026: nothing on this card scrolls); the body
 * keeps a scroll path only as the fit-or-scroll safety net, never as the layout. */
.quick {
  height: var(--h-talia-card);
  row-gap: var(--space-10px);
}

/* Compact (owner, 25 September 2026): the card was ~270 tall for a figure, a ring, its chips and
 * one line of words. A step off the padding and the row gap, a smaller figure and avatar, the words
 * on one line — and nothing stretched to fill. */
.short {
  gap: var(--space-2) var(--space-4);

  /* One inner padding on all four sides (owner, 25 September 2026): the foot had less room from the
   * bottom edge than the head had from the top. */
  padding: var(--space-4);
  background: var(--bg-inset);
  border-style: none;
  border-radius: var(--radius-lg);
}

.head {
  display: flex;
  grid-column: 1 / -1;
  gap: var(--space-10px);
  align-items: center;
  min-width: 0;
}

.short .head {
  gap: var(--space-10px);
  align-items: flex-start;
}

.avatar {
  --sz-avatar: 32px;
}

.who {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.name {
  overflow: hidden;
  font-size: var(--fs-button);
  font-weight: var(--fw-extrabold);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
}

.kind {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-2);
}

/* The head at main's feedback-row sizes (owner, 26 September 2026 — `ItemLines`' `tile` variant and
 * `FeedbackRowHead`): the name at `--fs-foot`, the kind at `--fs-micro`, the hour at `--fs-caption`. */
.short .name {
  font-size: var(--fs-foot);
  letter-spacing: var(--ls-heading);
}

.short .kind {
  font-size: var(--fs-micro);
  font-weight: var(--fw-regular);
}

/* The one meta line, muted, after the kind — and the room the age does not need. */
.glance {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-2);
}

/* The name and the kind as one tight pair beside the 32px avatar. The pair sums to 31.85; the box
 * holds a whole 32 so the reserve's two bars (`TaskTaliaCardReserve`) stand in exactly its height. */
.short .who {
  min-height: 32px;
}

.short .name,
.short .kind {
  line-height: var(--lh-normal);
}

.age {
  flex: none;
  margin-inline-start: auto;
  align-self: flex-start;
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.short .age {
  padding-block-start: var(--space-2px);
  font-weight: var(--fw-regular);
  font-variant-numeric: tabular-nums;
}

.body {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
}

/* The Pulpit card's body holds the poster's 112 whatever lands in it, so a session and a clip are
 * one height and the reserve stands for both; what is shorter is centred in it rather than packed
 * to the top over an empty band (owner, 26 September 2026). */
.short .body {
  justify-content: center;
  min-height: 112px;
}

.foot {
  display: flex;
  grid-column: 1 / -1;
  gap: var(--space-6px);
  align-items: center;
  min-width: 0;
  padding-block-start: var(--space-3);
  border-top: var(--border-hairline) solid var(--border);
}

.quick .foot {
  padding-block-start: var(--space-2);
}

.short .foot {
  padding-block-start: var(--space-0);
  border-top-style: none;
}

.after {
  grid-column: 1 / -1;
}

/* Nothing opened under the card: no row, and so no row gap spent on it. */
.after:empty {
  display: none;
}

/* ── The reserve's holes (`TaskTaliaCardReserve`) — each the size of what lands in it ─────────── */

.holeAvatar {
  flex: none;
  width: 32px;
  height: 32px;
}

.holeName {
  display: block;
  width: 140px;
  height: 13px;
}

.holeKind {
  display: block;
  width: 180px;
  height: 11px;
  margin-block-start: var(--space-1);
}

/* The body's hold (`.short .body`): the box a session's ring and column, and a clip's poster, stand in. */
.holeVisual {
  display: block;
  height: 112px;
}

.holeFoot {
  display: block;
  width: 100%;
  height: 30px;
}
